import { useNavigate, useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useEffect } from 'react';
import { ArrowLeft, ArrowRight, Calendar, Clock } from 'lucide-react';
import { ThemeToggle } from '../components/ThemeToggle';
import { trackEvent, Events } from '../lib/analytics';
import {
  getPostBySlug,
  getRelatedPosts,
  buildArticleSchema,
  buildBreadcrumbSchema,
  BLOG_SITE_URL,
} from '../data/blogPosts';

function renderSection(s, idx) {
  if (s.type === 'h2') {
    return (
      <h2
        key={idx}
        className="text-2xl sm:text-3xl font-bold text-foreground mt-12 mb-4 tracking-tight"
      >
        {s.text}
      </h2>
    );
  }
  if (s.type === 'h3') {
    return (
      <h3 key={idx} className="text-lg sm:text-xl font-semibold text-foreground mt-8 mb-3">
        {s.text}
      </h3>
    );
  }
  if (s.type === 'ul') {
    return (
      <ul key={idx} className="list-disc pl-6 space-y-2 my-4 text-foreground/85 leading-relaxed">
        {s.items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    );
  }
  if (s.type === 'quote') {
    return (
      <blockquote
        key={idx}
        className="border-l-4 border-primary pl-4 my-6 italic text-foreground/80"
      >
        {s.text}
      </blockquote>
    );
  }
  return (
    <p key={idx} className="text-base sm:text-[17px] text-foreground/85 leading-[1.75] my-4">
      {s.text}
    </p>
  );
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getPostBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4">
        <Helmet>
          <title>Post not found — Agent9 Blog</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="text-center" data-testid="blog-not-found">
          <h1 className="text-2xl font-bold mb-2">Post not found</h1>
          <p className="text-muted-foreground mb-6">
            We couldn’t find the article you were looking for.
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 text-primary hover:underline"
            data-testid="blog-not-found-back"
          >
            <ArrowLeft size={14} /> Back to blog
          </Link>
        </div>
      </div>
    );
  }

  const canonical = `${BLOG_SITE_URL}/blog/${post.slug}`;
  const schema = buildArticleSchema(post);
  const breadcrumb = buildBreadcrumbSchema([
    { name: 'Home', url: `${BLOG_SITE_URL}/` },
    { name: 'Blog', url: `${BLOG_SITE_URL}/blog` },
    { name: post.title, url: canonical },
  ]);
  const related = getRelatedPosts(post.slug, 2);

  return (
    <>
      <Helmet>
        <title>{post.seoTitle}</title>
        <meta name="description" content={post.description} />
        <meta name="keywords" content={post.keywords} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={post.seoTitle} />
        <meta property="og:description" content={post.description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:site_name" content="Agent9" />
        <meta property="article:published_time" content={post.publishedAt} />
        <meta property="article:author" content="Agent9" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.seoTitle} />
        <meta name="twitter:description" content={post.description} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      </Helmet>

      <div
        className="min-h-screen bg-background text-foreground"
        data-testid={`blog-post-${post.slug}`}
      >
        {/* Nav */}
        <nav className="fixed top-0 w-full z-50 bg-card/80 backdrop-blur-xl border-b border-border">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="blog-nav-home">
              <img src="/assets/agent9-logo.png" alt="Agent9" className="h-8 object-contain" />
            </Link>
            <div className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
              <Link to="/blog" className="hover:text-foreground transition-colors">Blog</Link>
              <Link to="/agent9" className="hover:text-foreground transition-colors">Agent9</Link>
              <Link to="/voicedeck" className="hover:text-foreground transition-colors">VoiceDeck</Link>
              <Link to="/pricing" className="hover:text-foreground transition-colors">Pricing</Link>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle compact />
              <button
                onClick={() => navigate('/signup')}
                className="text-sm bg-foreground hover:bg-foreground/90 text-background px-4 py-2 rounded-full font-medium"
                data-testid="blog-nav-signup"
              >
                Get Started
              </button>
            </div>
          </div>
        </nav>

        {/* Article */}
        <article className="pt-24 pb-12">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8"
              data-testid="back-to-blog"
            >
              <ArrowLeft size={14} /> Back to blog
            </Link>

            <header className="mb-8">
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mb-4">
                <span className="inline-flex items-center gap-1">
                  <Calendar size={12} />
                  {new Date(post.publishedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock size={12} /> {post.readingMinutes} min read
                </span>
                <span>By Agent9</span>
              </div>
              <h1
                className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1] text-foreground"
                data-testid="blog-post-title"
              >
                {post.title}
              </h1>
              <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
                {post.excerpt}
              </p>
            </header>

            <div className="border-t border-border pt-2">{post.body.map(renderSection)}</div>

            {/* Internal links band */}
            <div
              className="mt-12 rounded-2xl border border-border bg-muted/40 p-5 text-sm text-foreground/85"
              data-testid="blog-internal-links"
            >
              <p className="leading-relaxed">
                Want to see this in action? Explore our{' '}
                <Link to="/" className="text-primary font-medium hover:underline">
                  AI assistant for service businesses
                </Link>
                , dive into{' '}
                <Link to="/agent9" className="text-primary font-medium hover:underline">
                  Agent9 for trades and real estate
                </Link>
                , spin up a smart phone tree with{' '}
                <Link to="/voicedeck" className="text-primary font-medium hover:underline">
                  VoiceDeck IVR
                </Link>
                , or compare plans on the{' '}
                <Link to="/pricing" className="text-primary font-medium hover:underline">
                  Agent9 pricing page
                </Link>
                .
              </p>
            </div>

            {/* CTA */}
            <div
              className="mt-10 rounded-3xl bg-gradient-to-br from-cyan-400 via-sky-400 to-blue-500 dark:from-cyan-600 dark:via-sky-700 dark:to-blue-800 p-6 sm:p-10 text-white"
              data-testid="blog-cta"
            >
              <h2 className="text-xl sm:text-2xl font-bold mb-2">{post.cta.title}</h2>
              <p className="text-white/85 text-sm sm:text-base leading-relaxed max-w-2xl">
                {post.cta.body}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  onClick={() => {
                    trackEvent(Events.SIGNUP_CLICKED, { location: `blog-${post.slug}` });
                    navigate('/signup');
                  }}
                  className="inline-flex items-center gap-1.5 bg-white text-slate-900 hover:bg-white/90 px-5 py-2.5 rounded-full font-semibold text-sm transition-colors"
                  data-testid="blog-cta-trial"
                >
                  Start Free Trial <ArrowRight size={14} />
                </button>
                <button
                  onClick={() => {
                    trackEvent(Events.DEMO_MODAL_OPENED, { location: `blog-${post.slug}` });
                    navigate('/signup?demo=1');
                  }}
                  className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 border border-white/30 text-white px-5 py-2.5 rounded-full font-semibold text-sm transition-colors"
                  data-testid="blog-cta-demo"
                >
                  Book a Demo
                </button>
              </div>
            </div>

            {/* Related posts */}
            {related.length > 0 && (
              <section
                className="mt-12 pt-10 border-t border-border"
                data-testid="related-posts-section"
              >
                <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-5">
                  Related posts
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {related.map((rp) => (
                    <Link
                      key={rp.slug}
                      to={`/blog/${rp.slug}`}
                      className="block group rounded-2xl border border-border bg-card p-5 transition-colors hover:border-foreground/30"
                      data-testid={`related-post-${rp.slug}`}
                    >
                      <h3 className="text-base font-semibold text-foreground group-hover:text-primary transition-colors">
                        {rp.title}
                      </h3>
                      <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-3">
                        {rp.excerpt}
                      </p>
                      <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary">
                        Read more <ArrowRight size={13} />
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>

        <footer className="border-t border-border py-8 text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} Agent9. All rights reserved.
        </footer>
      </div>
    </>
  );
}
